import {
  cloneMap, DataModel, DataModelBuilder,
  isSameData, isSameMap, mapToData, SerializationContext, SerializedData,
} from '../dto/dto.js';
import { MapUtils } from '../../utility/map-utils.js';
import { isNotNullOrUndefined } from '../../utility/null-safety.js';
import { VersionRange } from '../core/version-range.js';
import { PaymentProvider } from './payment-provider.js';
import { PaymentStatus } from './payment-status.js';
import { CustomerOrderBase } from '../../contracts/customer-order.js';

export class PaymentRequestResult extends DataModel {
  static readonly TYPE_ID: string = 'a9a948152d114df984b15cbdd1cf7acb';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
    public readonly order: CustomerOrderBase,
    public readonly status: PaymentStatus,
    public readonly provider: PaymentProvider,
    public readonly paymentRequestId: string | null = null,
    public readonly paymentId: string | null = null,
    public readonly data: Map<string, unknown> = new Map<string, unknown>(),
  ) {
    super();
  }

  get $typeId() {
    return PaymentRequestResult.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, PaymentRequestResult.SCHEMA_VERSION);
  }

  get isFailure() {
    return this.status === PaymentStatus.Failed || this.status === PaymentStatus.Canceled;
  }

  static fromMap(context: SerializationContext, map: SerializedData): PaymentRequestResult {
    return new PaymentRequestResult(
        context.fromData<CustomerOrderBase>(MapUtils.get<SerializedData>(map, 'order')),
        MapUtils.get<PaymentStatus>(map, 'status'),
        MapUtils.get<PaymentProvider>(map, 'provider'),
        MapUtils.getOrNull<string>(map, 'paymentRequestId'),
        MapUtils.getOrNull<string>(map, 'paymentId'),
        MapUtils.getMap(map, 'data', (k, v) => {
          return { key: k, value: v };
        }),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'order': context.toData(this.order),
      'status': this.status,
      'provider': this.provider,
      'paymentRequestId': this.paymentRequestId,
      'paymentId': this.paymentId,
      'data': mapToData(this.data, (k, v) => {
        return { key: k, value: v };
      }),
    };
  }

  clone(): this {
    return new PaymentRequestResult(
        this.order.clone(),
        this.status,
        this.provider,
        this.paymentRequestId,
        this.paymentId,
        cloneMap(this.data),
    ) as this;
  }

  isSame(other: PaymentRequestResult): boolean {
    if(this === other) {
      return true;
    }

    return isSameData(other.order, this.order) &&
      isSameMap(other.data, this.data) &&
      this.status === other.status &&
      this.provider === other.provider &&
      this.paymentRequestId === other.paymentRequestId &&
      this.paymentId === other.paymentId;
  }

  getData<T>(key: string, defValue?: T): T {
    const value = this.data.get(key);

    if(isNotNullOrUndefined(value)) {
      return value as T;
    }

    if(isNotNullOrUndefined(defValue)) {
      return defValue;
    }

    throw new Error(`Missing or null field with key {${key}} in payment data.`);
  }

  getDataOrNull<T>(key: string): T | null {
    const value = this.data.get(key);
    // eslint-disable-next-line no-undefined
    if(value !== undefined) {
      if(value === null) return null;
      return value as T;
    }

    return null;
  }
}

export class PaymentRequestResultBuilder extends DataModelBuilder<PaymentRequestResult> {
  build(context: SerializationContext, map: SerializedData): PaymentRequestResult {
    return PaymentRequestResult.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(PaymentRequestResult.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(PaymentRequestResult.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version the [paymentRequestId] field was added.
    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, 'paymentRequestId', null);
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
